import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as SimplrUserAuthorization from '@asurion-core/typescript-utils/SimplrUserAuthorization';
import {
  extractOwnAvailability,
  isSpecialistLite,
  isSpecialist,
  isAccountUser as isAccountUserSelector,
  getShowAppVersionRefreshNotification,
  isSuperAdmin,
  getAllowedPolicyForResource,
  getSimplrAccountIdOfSelectedTicket,
  getPartnersAllowlist,
  getFlowSystemLinkFromDebugState,
} from '../../extractFromState';
import { updateOwnAvailability, updateExpert } from '../../actions/creatorsSync';
import { AVAILABLE, MANUAL_UNAVAILABLE } from '../../constants/expertAvailability';
import { getLoggedInExpert, makeAvailable, makeManualUnavailable } from 'api/expert';
import { logout } from '../../api/user';

import {
  updateAvailabilitySwitchLockExp,
  updateExpertToggleLockConfirmation,
  setChatAlertActiveId,
} from '../../actions';
import { showError, showTimerPopupError } from '../../util/errorPopupUtil';
import ownExpertLoader from '../../loaders/ownExpertLoader';
import * as expertTypes from 'constants/expertTypes';
import IconButton from '../common/IconButton/IconButton';
import moment from 'moment';
import { calculateRemainingTime } from '../../util/util';
import { PenaltyBoxError } from '../../util/error';
import { CloudFunctionRequestError } from '@asurion-core/frontend-utils/lib/ErrorsModule/ErrorsModule';
import { showExpertToggleLockPopupComponent } from '../popups/ToggleLockPopup';
import AppVersionRefreshNotification from '../AppVersionRefreshNotification/AppVersionRefreshNotification';
import Tooltip from 'rc-tooltip';
import TicketsInProgress from 'components/TicketsInProgress/TicketsInProgress';
import NewChatAlert from '../NewChatAlert/NewChatAlert';
import environment from 'environment';
import { shareSessionTokenWithAdminDashboard } from 'util/devUtils';
import { useTheme } from 'context/ThemeContext';
import {
  BottomButtonsWrapper,
  DarkModeSwitch,
  Label,
  ToggleSwitch,
  HeaderBox,
  RestartSectionWrapper,
  TooltipOverlay,
} from './Header.styles';

const DarkModeToggle = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <>
      <DarkModeSwitch type="checkbox" checked={isDarkMode} onChange={toggleTheme} />
      <Label>Mode</Label>
    </>
  );
};

const getLogicBuilderUrl = ({ flowSystemLinkFromDebugState, selectedTicketAccountId, partnersAllowlist }) => {
  if (flowSystemLinkFromDebugState) {
    return `${environment.logicBuilderUrl}${flowSystemLinkFromDebugState}`;
  }

  const accountId = selectedTicketAccountId || (partnersAllowlist?.length === 1 ? partnersAllowlist[0] : null);

  if (accountId) {
    return `${environment.logicBuilderUrl}${accountId}/`;
  }

  return environment.logicBuilderUrl;
};

export class Header extends React.Component {
  state = {
    isAvailable: false,
    refreshClicked: false,
    toggleActive: true,
  };

  componentDidMount() {
    // on refresh
    this.setAvailabilitySwitchLockExp(true);

    // shares session token and user data with admin-dashboard when apps run on localhost
    shareSessionTokenWithAdminDashboard();
  }

  UNSAFE_componentWillMount() {
    // on sign in
    this.setAvailabilitySwitchLockExp(true);
  }

  async setAvailabilitySwitchLockExp(onInitialization = false) {
    const { availabilitySwitchLockExp, expertToggleLockConfirmation, availabilityStatus } = await getLoggedInExpert();

    if (!onInitialization) {
      this.props.updateOwnAvailability(availabilityStatus);
    }

    if (availabilitySwitchLockExp) {
      this.props.updateAvailabilitySwitchLockExp(availabilitySwitchLockExp);
    }

    this.props.updateExpertToggleLockConfirmation(expertToggleLockConfirmation);
  }

  isAvailabilitySwitchLockExpired() {
    const { availabilitySwitchLockExp } = this.props;

    if (availabilitySwitchLockExp) {
      const currentTime = moment.utc();
      const availabilitySwitchLockExpTime = moment.utc(availabilitySwitchLockExp);

      return currentTime.isAfter(availabilitySwitchLockExpTime);
    }

    return true;
  }

  toggleAvailability = async (isAvailable) => {
    this.setState({ toggleActive: false });

    this.props.setChatAlertActiveId(null); // backup kill switch for blocking backround

    const isLockExpired = this.isAvailabilitySwitchLockExpired();

    let remainingTime = calculateRemainingTime(this.props.availabilitySwitchLockExp);

    const availabilityStatus = isAvailable ? MANUAL_UNAVAILABLE : AVAILABLE;

    this.props.updateOwnAvailability(availabilityStatus);

    try {
      if (isAvailable) {
        if (this.props.expertToggleLockConfirmation) {
          await makeManualUnavailable();
        } else {
          this.props.updateOwnAvailability(isAvailable ? AVAILABLE : MANUAL_UNAVAILABLE);
          const popupPromise = new Promise((resolve, reject) => {
            showExpertToggleLockPopupComponent(this.props.simplrExpertId, true, resolve);
          });
          await popupPromise;
        }
        await this.setAvailabilitySwitchLockExp();
      } else if (isLockExpired) {
        await makeAvailable();
      } else {
        throw new PenaltyBoxError(
          `You cannot change your status to online.`,
          `You will be able to go online in ${remainingTime.minutes}:${remainingTime.seconds}`,
          this.props.availabilitySwitchLockExp
        );
      }
    } catch (error) {
      // revert since update probably didn't persist
      const unchangedAvailabilityStatus = isAvailable ? AVAILABLE : MANUAL_UNAVAILABLE;
      this.props.updateOwnAvailability(unchangedAvailabilityStatus);
      if (error instanceof CloudFunctionRequestError) {
        showError(error);
      } else if (error instanceof PenaltyBoxError) {
        showTimerPopupError(error, true);
      }
    } finally {
      this.setState({ toggleActive: true });
    }
  };

  handleRestartClick = () => {
    this.setState({ refreshClicked: true });
    window.location.reload(true);
  };

  getToolTipContent() {
    return <TooltipOverlay>Restarting the application will erase any unsent messages.</TooltipOverlay>;
  }
  render() {
    const {
      isAdmin,
      _isSpecialistLite,
      _isAccountUser,
      user,
      availability,
      location,
      showRefreshNotification,
      _isSuperAdmin,
      allowedPolicyForSimplrflexDashboard,
    } = this.props;
    const { toggleActive } = this.state;

    const isExpertAvailable = availability === AVAILABLE;
    const isAdminPortalActive = ['/admin', '/kb'].includes(location.pathname);
    const isSpecialistOrSuperAdmin = _isSuperAdmin || isAdmin;
    const isActiveExpert = user.expertType === expertTypes.LIVE;
    const canShowForNonAdmin = !isAdmin && !_isSpecialistLite;
    const canShowForSpecialistOrSuperAdmin = isSpecialistOrSuperAdmin && !!allowedPolicyForSimplrflexDashboard;
    const showAdminPortalButton = isAdmin && !_isSpecialistLite && !_isAccountUser;
    const showLogicBuilderButton = environment.logicBuilderUrl && ((isAdmin && !_isSpecialistLite) || _isAccountUser);
    const logicBuilderUrl = getLogicBuilderUrl(this.props);
    const showExpertDashboardButton =
      user.loggedIn && !!isActiveExpert && (canShowForNonAdmin || canShowForSpecialistOrSuperAdmin);

    return (
      !isAdminPortalActive && (
        <HeaderBox className="top-navbar">
          <IconButton src="/images/new-designs/simplr-logo-new.svg" to="/home" isLogo />
          {!isAdmin && (
            <>
              <ToggleSwitch
                id="availabilitySwitch"
                type="checkbox"
                disabled={!toggleActive}
                checked={isExpertAvailable}
                onChange={() => this.toggleAvailability(isExpertAvailable)}
              />
              <Label>{isExpertAvailable ? 'Online' : 'Offline'}</Label>
            </>
          )}
          {showAdminPortalButton && (
            <>
              <IconButton
                to={environment.adminDashboardUrl}
                src="/images/new-designs/admin-ui-icon.svg"
                hoverSrc="/images/new-designs/admin-ui-icon-hover.svg"
                label="Admin"
                topBorder
                target="_self"
                isExternal
              />
            </>
          )}
          {showLogicBuilderButton && (
            <IconButton
              to={logicBuilderUrl}
              src="/images/new-designs/logic-builder-icon.svg"
              hoverSrc="/images/new-designs/logic-builder-icon-hover.svg"
              label="Logic Builder"
              topBorder
              target="_blank"
              rel="noopener noreferrer"
              isExternal
            />
          )}
          {showExpertDashboardButton && (
            <IconButton
              to="/home/expert-dashboard"
              src="/images/new-designs/simplrflex-dashboard-icon.svg"
              hoverSrc="/images/new-designs/simplrflex-dashboard-icon-hover.svg"
              label="Expert Dashboard"
              topBorder
            />
          )}
          <Tooltip
            overlayClassName={showRefreshNotification ? 'hidden' : 'expert-widget-stats-tooltip'}
            arrowContent={<div className="rc-tooltip-arrow-inner" />}
            placement="right"
            overlay={this.getToolTipContent()}
          >
            <RestartSectionWrapper>
              <IconButton
                label="Restart"
                onClick={() => this.handleRestartClick()}
                src="/images/new-designs/restart-icon.svg"
                hoverSrc="/images/new-designs/restart-icon-hover.svg"
                withBorders
                showSpinner={this.state.refreshClicked}
              />
              <AppVersionRefreshNotification />
            </RestartSectionWrapper>
          </Tooltip>

          <NewChatAlert />

          {!!isActiveExpert && !isAdmin && <TicketsInProgress />}

          <BottomButtonsWrapper>
            <DarkModeToggle />
            <div className="intercom-launcher">
              <div className="intercom-icon-close" />
              <div className="intercom-icon-open" />
              <span className="intercom-unread-count" />
            </div>
            <Label>Help</Label>
            <IconButton
              withBorders
              label="Logout"
              src="/images/new-designs/logout-icon.svg"
              hoverSrc="/images/new-designs/logout-icon-hover.svg"
              onClick={() =>
                this.props.onManualLogout(
                  this.isAvailabilitySwitchLockExpired(),
                  this.props.simplrExpertId,
                  this.props.expertToggleLockConfirmation,
                  this.props.isAdmin,
                  isExpertAvailable
                )
              }
            />
          </BottomButtonsWrapper>
        </HeaderBox>
      )
    );
  }
}

Header.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  _isSpecialistLite: isSpecialistLite(state),
  _isAccountUser: isAccountUserSelector(state),
  isAdmin: isSpecialist(state),
  _isSuperAdmin: isSuperAdmin(state),
  user: state.user,
  availabilitySwitchLockExp: state.user.availabilitySwitchLockExp,
  expertToggleLockConfirmation: state.user.expertToggleLockConfirmation,
  simplrExpertId: state.user.id, // used in ownExpertLoader
  availability: extractOwnAvailability(state),
  showRefreshNotification: getShowAppVersionRefreshNotification(state),
  allowedPolicyForSimplrflexDashboard: getAllowedPolicyForResource(
    state,
    SimplrUserAuthorization.POLICY_RESOURCES.SIMPLRFLEX_DASHBOARD
  ),
  selectedTicketAccountId: getSimplrAccountIdOfSelectedTicket(state),
  partnersAllowlist: getPartnersAllowlist(state),
  flowSystemLinkFromDebugState: getFlowSystemLinkFromDebugState(state),
});

const mapDispatchToProps = (dispatch) => {
  return {
    updateExpert: compose(dispatch, updateExpert), // used in ownExpertLoader
    onManualLogout: async (isLockExpired, simplrExpertId, expertToggleLockConfirmation, isAdmin, isExpertAvailable) => {
      if (isLockExpired && !expertToggleLockConfirmation && !isAdmin && isExpertAvailable) {
        showExpertToggleLockPopupComponent(simplrExpertId);
      } else if (isLockExpired && !isAdmin && isExpertAvailable) {
        await makeManualUnavailable();
        await logout();
      } else {
        await logout();
      }
    },
    updateExpertToggleLockConfirmation: (value) => dispatch(updateExpertToggleLockConfirmation(value)),
    updateAvailabilitySwitchLockExp: (value) => dispatch(updateAvailabilitySwitchLockExp(value)),
    updateOwnAvailability: (availability) => dispatch(updateOwnAvailability(availability)),
    setChatAlertActiveId: (activeId) => dispatch(setChatAlertActiveId(activeId)),
  };
};

const HeaderNonAdminLoader = ownExpertLoader(Header);

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    // ownExpertLoader depends on simplrExpertId
    (props) => (!props.isAdmin && props.simplrExpertId ? <HeaderNonAdminLoader {...props} /> : <Header {...props} />)
  )
);
